import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';

import type { Site } from '@klareospace/pharos-lib-types';
import {
  DateRangeFilter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';
import type { DateRangePreset, DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import { useCarriersForSelect } from '&/features/settings/carriers/hooks';

import type { OrderCreationFormInput } from '../../schemas/order-creation.schema';
import { buildDefaultTimeSlotValue } from '../../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SchedulingSectionProps {
  /** The pickup site — scopes the carrier list and the default delivery timeslot duration. */
  selectedSite: Site | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SCHEDULING_PRESETS: DateRangePreset[] = ['today', 'tomorrow', 'custom'];
/**
 * Radix Select rejects an empty string item value — mapped to/from `carrierCode: ''` ("Not
 * assigned"). Mirrors the `NONE_CARRIER_VALUE`/`NONE_DRIVER_VALUE` sentinel convention
 * (`features/tasks/utils/task-assignment.ts`) rather than introducing a new sentinel string.
 */
const NOT_ASSIGNED_CARRIER_VALUE = '__none__';

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "Scheduling" section body — delivery timeslot (`DateRangeFilter`, pre-filled with the next
 * available slot) + an optional carrier select scoped to the pickup site. Form-state only; order
 * submission ships in a later ticket.
 */
export function SchedulingSection({ selectedSite }: SchedulingSectionProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');
  const { control, formState, setValue } = useFormContext<OrderCreationFormInput>();
  const errorMessage = formState.errors.scheduling?.message;

  const deliveryTimeSlot = useWatch({ control, name: 'scheduling.deliveryTimeSlot' });
  const carrierCode = useWatch({ control, name: 'scheduling.carrierCode' });
  const carrierOptions = useCarriersForSelect({ enabledOnSiteId: selectedSite?.id, valueKey: 'code' });

  // Falls back to a freshly-computed default for render only (never written to form state here) —
  // the drawer's own seeding effect sets the real value the moment this section becomes active, so
  // this only covers the brief render before that effect has run.
  const timeSlotValue = deliveryTimeSlot ?? buildDefaultTimeSlotValue(selectedSite, 'today');

  function handleTimeSlotChange(value: DateRangeValue): void {
    setValue('scheduling.deliveryTimeSlot', value, { shouldValidate: true });
  }

  function handleCarrierChange(value: string): void {
    const nextCode = value === NOT_ASSIGNED_CARRIER_VALUE ? '' : value;
    setValue('scheduling.carrierCode', nextCode, { shouldValidate: true });
  }

  return (
    <div className="space-y-4">
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground" htmlFor="scheduling-carrier">
          {tCommon('fields.carrier')}
        </label>
        <Select onValueChange={handleCarrierChange} value={carrierCode || NOT_ASSIGNED_CARRIER_VALUE}>
          <SelectTrigger aria-label={tCommon('fields.carrier')} id="scheduling-carrier">
            <SelectValue placeholder={t('sections.scheduling.fields.carrier.notAssigned')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NOT_ASSIGNED_CARRIER_VALUE}>
              {t('sections.scheduling.fields.carrier.notAssigned')}
            </SelectItem>
            {carrierOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DateRangeFilter
        label={t('sections.scheduling.fields.deliveryTimeSlot.label')}
        onChange={handleTimeSlotChange}
        presets={SCHEDULING_PRESETS}
        showTimeRange
        value={timeSlotValue}
      />
    </div>
  );
}
